import { Fragment, useMemo, useState } from "react";
import { Breadcrumbs } from "../../../AbstractElements";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import DataTable from "../../Components/DataTable";
import { useNavigate } from "react-router";
import "sweetalert2/dist/sweetalert2.min.css";
import { FormFilterOrder } from "../../Components/order/FormFilterOrder";
import { getOrderColumns } from "../../Components/order/order.columns";
import { API_BASE_URL } from "../../api/api";
import { PaymentProofModal } from "../../Components/order/PaymentProofModal"; 
import StatsCards from "../../Components/order/StatsCards";
import PendingShipmentModal from "../../Components/order/PendingShipmentModal";
import { useAdmin } from "../../../context/AdminContext";

export const OrderList = () => {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [wompiReference, setWompiReference] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    country: "",
    user: "",
    payment_method: "",
    wompi_reference: "",
  });

  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [proofOrder, setProofOrder] = useState(null);

  const [pendingShipmentModalOpen, setPendingShipmentModalOpen] =
    useState(false);

  const { getData } = useDataFetch();
  const { admin } = useAdmin();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { data, isLoading } = getData(
    ["order", filters, pagination],
    "/order",
    {
      enabled: true,
    },
    {
      status: filters.status || undefined,
      country: filters.country || undefined,
      user: filters.user || undefined,
      order_id: filters.order_id || undefined,
      payment_method: filters.payment_method || undefined,
      wompi_reference: filters.wompi_reference || undefined,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      start_date: dateRange[0]
        ? dateRange[0].toISOString().split("T")[0]
        : undefined,
      end_date: dateRange[1]
        ? dateRange[1].toISOString().split("T")[0]
        : undefined,
    }
  );

  const openPaymentProof = (order) => {
    setProofOrder(order);
    setProofModalOpen(true);
  };

  const closePaymentProof = () => {
    setProofModalOpen(false);
    setProofOrder(null);
  };

  const openPendingShipmentModal = () => {
    setPendingShipmentModalOpen(true);
  };

  const closePendingShipmentModal = () => {
    setPendingShipmentModalOpen(false);
  };

  const columns = useMemo(
    () => getOrderColumns({ openPaymentProof }),
    [openPaymentProof]
  );

  const handleSearch = () => {
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });

    setFilters({
      status: statusFilter,
      country: countryFilter,
      user: userFilter,
      start_date: dateRange[0]
        ? dateRange[0].toISOString().split("T")[0]
        : "",
      end_date: dateRange[1]
        ? dateRange[1].toISOString().split("T")[0]
        : "",
      payment_method: paymentMethodFilter,
      wompi_reference: wompiReference,
      order_id: orderIdFilter,
    });
  };

  const handleGenerateExcel = () => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;

      if (typeof value === "object" && value?.value) {
        params.set(key, value.value);
      } else {
        params.set(key, value);
      }
    });

    if (dateRange[0]) {
      params.set(
        "start_date",
        dateRange[0].toISOString().split("T")[0]
      );
    }

    if (dateRange[1]) {
      params.set(
        "end_date",
        dateRange[1].toISOString().split("T")[0]
      );
    }

    const url = `${API_BASE_URL}/order/report/excel?${params.toString()}`;

    window.open(url, "_blank");
  };


  const orders = data?.data?.orders ?? [];
  const totalOrders = data?.data?.total ?? 0;
  const totalSales = data?.data?.netSales ?? 0;
  const totalBonusPoints = data?.data?.totalBonusPoints ?? 0;
  const totalRoyaltyPoints = data?.data?.totalRoyaltyPoints ?? 0;

  return (
    <Fragment>
      <Breadcrumbs
        parent="Tienda"
        title="Órdenes"
        mainTitle="Órdenes"
      />

      <Container fluid={true}>
        <Row>

          {["ADMIN","ADMIN_ASSISTANT"].includes(admin?.role)&&
          <Col sm="12" className="mb-3">
            <StatsCards 
              totalOrders={totalOrders}
              totalSales={totalSales}
              totalBonusPoints={totalBonusPoints}
              totalRoyaltyPoints={totalRoyaltyPoints}
            />
          </Col>}
          
          <Col sm="12">
            <Card>
              <CardBody>
                <FormFilterOrder
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  countryFilter={countryFilter}
                  setCountryFilter={setCountryFilter}
                  userFilter={userFilter}
                  setUserFilter={setUserFilter}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  handleSearch={handleSearch}
                  handleGenerateExcel={handleGenerateExcel}
                  paymentMethodFilter={paymentMethodFilter}
                  setPaymentMethodFilter={setPaymentMethodFilter}
                  onRegisterOrder={() => navigate("/order/create")}
                  onPendingShipments={openPendingShipmentModal}
                  wompiReference={wompiReference}
                  setWompiReference={setWompiReference}
                  orderIdFilter={orderIdFilter}
                  setOrderIdFilter={setOrderIdFilter}
                />

                <DataTable
                  data={data?.data?.orders ?? []}
                  total={data?.data?.total ?? 0}
                  pagination={pagination}
                  setPagination={setPagination}
                  columns={columns}
                  progressPending={isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {proofModalOpen && (
        <PaymentProofModal
          isOpen={proofModalOpen}
          toggle={closePaymentProof}
          order={proofOrder}
        />
      )}

      {pendingShipmentModalOpen && (
        <PendingShipmentModal
          isOpen={pendingShipmentModalOpen}
          toggle={closePendingShipmentModal}
        />
      )}
    </Fragment>
  );
};